import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Loader2, TrendingUp, DollarSign, Swords, ArrowRight, Gauge } from "lucide-react";
import { toast } from "sonner";
import i18n from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EXAMPLES = ["Meditation for gamers", "Finance for teens", "AI meal prep"];

const verdictColor = {
  "High Potential": "text-[#00F0FF]",
  Promising: "text-emerald-400",
  Crowded: "text-amber-400",
};

export default function NicheFinder() {
  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const analyze = async (text) => {
    const q = (text ?? topic).trim();
    if (!q || loading) return;
    setTopic(q);
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/niche`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ topic: q, lang: i18n.language }),
      });
      if (!res.ok) throw new Error();
      setResult(await res.json());
    } catch {
      toast.error("Analysis failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const buildFromNiche = () => {
    const idea = result?.app_ideas?.[0] || topic;
    window.dispatchEvent(new CustomEvent("arca-build", { detail: `Build this app: ${idea}` }));
  };

  return (
    <section id="niche" className="py-28 md:py-36 border-t border-white/10 relative overflow-hidden">
      <div className="pointer-events-none absolute -left-32 top-1/2 w-[34rem] h-[34rem] bg-[#0055FF]/10 blur-[130px] rounded-full" />
      <div className="max-w-4xl mx-auto px-6 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#00F0FF] mb-4">/ niche finder</p>
          <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight">Discover profitable ideas instantly</h2>
          <p className="text-slate-400 mt-4 max-w-xl mx-auto">Type any niche. Our analyst scores its market potential with real revenue and competition signals.</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-6 md:p-8"
          data-testid="niche-finder"
        >
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 flex items-center gap-3 bg-black/40 border border-white/10 rounded-2xl px-5 focus-within:border-[#0055FF] transition-colors duration-300">
              <Search size={18} className="text-slate-500 shrink-0" />
              <input
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && analyze()}
                placeholder="e.g. meditation for gamers"
                className="flex-1 bg-transparent outline-none py-4 text-white placeholder:text-slate-600"
                data-testid="niche-input"
              />
            </div>
            <button
              onClick={() => analyze()}
              disabled={loading || !topic.trim()}
              className="inline-flex items-center justify-center gap-2 bg-[#0055FF] text-white px-8 py-4 rounded-2xl font-semibold shadow-[0_0_30px_rgba(0,85,255,0.45)] disabled:opacity-40 disabled:shadow-none hover:-translate-y-0.5 transition-transform duration-300"
              data-testid="niche-analyze-btn"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <TrendingUp size={18} />}
              Analyze
            </button>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            {EXAMPLES.map((e, i) => (
              <button
                key={i}
                onClick={() => analyze(e)}
                disabled={loading}
                className="text-xs font-mono text-slate-500 border border-white/10 rounded-full px-3 py-1.5 hover:text-white hover:border-white/25 transition-colors duration-300 disabled:opacity-40"
                data-testid={`niche-example-${i}`}
              >
                {e}
              </button>
            ))}
          </div>

          <AnimatePresence>
            {result && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.5 }}
                className="overflow-hidden"
                data-testid="niche-result"
              >
                <div className="mt-8 p-6 md:p-8 bg-black/40 rounded-2xl border border-[#0055FF]/30">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-1">Verdict</p>
                      <p className={`font-display font-bold text-2xl ${verdictColor[result.verdict] || "text-white"}`}>
                        {result.verdict}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Gauge size={20} className="text-[#00F0FF]" />
                      <span className="font-display font-extrabold text-4xl">{result.score}</span>
                      <span className="text-slate-500 text-lg">/10</span>
                    </div>
                  </div>

                  <p className="text-slate-300 mt-5 leading-relaxed">{result.summary}</p>

                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <div className="rounded-xl border border-white/10 p-4">
                      <div className="flex items-center gap-2 text-slate-500 text-xs font-mono uppercase tracking-widest mb-1">
                        <DollarSign size={13} /> Est. Revenue
                      </div>
                      <div className="font-display font-bold text-lg text-white">{result.revenue}</div>
                    </div>
                    <div className="rounded-xl border border-white/10 p-4">
                      <div className="flex items-center gap-2 text-slate-500 text-xs font-mono uppercase tracking-widest mb-1">
                        <Swords size={13} /> Competition
                      </div>
                      <div className="font-display font-bold text-lg text-white">{result.competition}</div>
                    </div>
                  </div>

                  {result.app_ideas?.length > 0 && (
                    <div className="mt-6">
                      <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-3">App concepts</p>
                      <ul className="space-y-2">
                        {result.app_ideas.map((idea, i) => (
                          <li key={i} className="flex items-start gap-2 text-slate-300 text-sm">
                            <span className="text-[#00F0FF] mt-1">▸</span> {idea}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <button
                    onClick={buildFromNiche}
                    className="group mt-8 inline-flex items-center gap-2 bg-white text-black px-7 py-3.5 rounded-2xl font-semibold hover:bg-[#00F0FF] transition-colors duration-300"
                    data-testid="niche-build-btn"
                  >
                    Build app from this niche
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
